import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { FileText, ChevronRight, ChevronLeft, Users, User } from "lucide-react";

const COLUMNS = [
  { key: "new",        label: "New",        dot: "#8a7e6d" },
  { key: "contacted",  label: "Contacted",  dot: "#d4a04a" },
  { key: "replied",    label: "Replied",    dot: "#b8963e" },
  { key: "audit_sent", label: "Audit Sent", dot: "#6db66d" },
  { key: "closed",     label: "Closed",     dot: "#5e5448" },
];

function tierColor(label) {
  return {
    Invisible:           "#c46a5a",
    "Under-represented": "#d4a04a",
    "Underrepresented": "#d4a04a",
    Emerging:            "#b8963e",
    Recommended:         "#6db66d",
  }[label] || "#5e5448";
}

export default function Pipeline() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [scope, setScope] = useState(isAdmin ? "team" : "me");
  const [audits, setAudits] = useState(null);
  const [savingId, setSavingId] = useState(null);

  const load = async () => {
    try {
      const url = (isAdmin && scope === "team") ? "/admin/audits" : "/audits";
      const { data } = await api.get(url);
      setAudits(data);
    } catch {}
  };

  useEffect(() => { load(); }, [scope, isAdmin]); // eslint-disable-line

  const grouped = useMemo(() => {
    const out = Object.fromEntries(COLUMNS.map((c) => [c.key, []]));
    (audits || []).forEach((a) => {
      const k = a.pipeline_status || "new";
      (out[k] || out.new).push(a);
    });
    return out;
  }, [audits]);

  const moveAudit = async (id, direction) => {
    const a = (audits || []).find((x) => x.id === id);
    if (!a) return;
    const currentIdx = COLUMNS.findIndex((c) => c.key === (a.pipeline_status || "new"));
    const nextIdx = Math.max(0, Math.min(COLUMNS.length - 1, currentIdx + direction));
    if (nextIdx === currentIdx) return;
    const nextStatus = COLUMNS[nextIdx].key;
    setAudits((prev) =>
      (prev || []).map((x) => (x.id === id ? { ...x, pipeline_status: nextStatus } : x))
    );
    setSavingId(id);
    try {
      await api.patch(`/audits/${id}/pipeline`, { pipeline_status: nextStatus });
    } catch {
      load();
    }
    setSavingId(null);
  };

  const total = audits?.length ?? 0;

  return (
    <div className="max-w-[1500px] mx-auto px-6 sm:px-8 py-14" data-testid="pipeline-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="overline">Pipeline</p>
          <h1 className="mt-3 font-serif-display text-4xl sm:text-5xl text-[#f4efe6] leading-[1.05]" style={{fontWeight: 700}}>
            Outreach <em className="text-[#b8963e] italic font-normal">pipeline.</em>
          </h1>
          <p className="mt-3 text-[#c9bca4]">
            Track every snapshot through your outreach motion — from cold lead to closed audit.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {isAdmin && (
            <div
              className="inline-flex items-center rounded-sm border border-[#2a2620] overflow-hidden"
              data-testid="pipeline-scope-toggle"
            >
              <button
                onClick={() => setScope("me")}
                data-testid="pipeline-scope-me"
                className="flex items-center gap-1.5 px-3 py-2 text-xs font-mono-eav tracking-wider uppercase transition-colors"
                style={{
                  background: scope === "me" ? "rgba(184, 150, 62, 0.12)" : "transparent",
                  color: scope === "me" ? "#b8963e" : "#8a7e6d",
                }}
              >
                <User className="w-3.5 h-3.5" /> Mine
              </button>
              <button
                onClick={() => setScope("team")}
                data-testid="pipeline-scope-team"
                className="flex items-center gap-1.5 px-3 py-2 text-xs font-mono-eav tracking-wider uppercase transition-colors border-l border-[#2a2620]"
                style={{
                  background: scope === "team" ? "rgba(184, 150, 62, 0.12)" : "transparent",
                  color: scope === "team" ? "#b8963e" : "#8a7e6d",
                }}
              >
                <Users className="w-3.5 h-3.5" /> Team
              </button>
            </div>
          )}
          <Link to="/audit/new" data-testid="pipeline-new-snapshot" className="btn-gold">
            New Snapshot
          </Link>
        </div>
      </div>

      {/* Column counts */}
      <div className="mt-10 grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="pipeline-stats">
        {COLUMNS.map((col) => (
          <div key={col.key} className="eav-card p-4">
            <div className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: col.dot }} />
              <span className="font-mono-eav text-[10px] tracking-[0.22em] text-[#8a7e6d] uppercase">
                {col.label}
              </span>
            </div>
            <p className="mt-2 font-serif-display font-bold text-3xl text-[#f4efe6]">
              {grouped[col.key]?.length ?? 0}
            </p>
          </div>
        ))}
      </div>

      {audits && audits.length === 0 && (
        <div className="mt-12 eav-card p-14 text-center" data-testid="pipeline-empty">
          <FileText className="w-8 h-8 text-[#5e5448] mx-auto" />
          <p className="mt-5 text-[#f4efe6] font-serif-display font-bold text-xl">Pipeline is empty</p>
          <p className="mt-2 text-sm text-[#8a7e6d]">
            Run your first AI Visibility Snapshot to add a lead to your pipeline.
          </p>
          <Link to="/audit/new" data-testid="pipeline-empty-cta" className="mt-6 inline-flex btn-gold">
            Run a snapshot
          </Link>
        </div>
      )}

      {!audits && (
        <div className="mt-12 text-[#8a7e6d] font-mono-eav text-sm" data-testid="pipeline-loading">
          Loading…
        </div>
      )}

      {audits && audits.length > 0 && (
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4" data-testid="pipeline-board">
          {COLUMNS.map((col) => (
            <div
              key={col.key}
              className="bg-[#0a0908] border border-[#2a2620] rounded-md p-3 min-h-[400px]"
              data-testid={`pipeline-column-${col.key}`}
            >
              {/* Header */}
              <div className="flex items-center justify-between px-2 py-2 mb-3 border-b border-[#1a1815]">
                <div className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: col.dot }} />
                  <span className="font-mono-eav text-[11px] tracking-[0.22em] uppercase font-semibold text-[#c9bca4]">
                    {col.label}
                  </span>
                </div>
                <span className="font-mono-eav text-[11px] text-[#5e5448]">
                  {grouped[col.key]?.length ?? 0}
                </span>
              </div>

              {/* Cards */}
              <div className="space-y-2">
                {grouped[col.key]?.length === 0 ? (
                  <div className="text-center py-8 text-[#3a352c] font-mono-eav text-[10px] tracking-wider uppercase">
                    Empty
                  </div>
                ) : (
                  grouped[col.key].map((a) => {
                    const colIdx = COLUMNS.findIndex((c) => c.key === col.key);
                    const tcolor = tierColor(a.tier_label);
                    return (
                      <div
                        key={a.id}
                        data-testid={`pipeline-card-${a.id}`}
                        className={`group eav-card p-3 transition-all ${savingId === a.id ? "opacity-50" : ""} hover:border-[#b8963e]/50`}
                      >
                        <Link
                          to={a.status === "complete" ? `/snapshot/${a.id}` : `/processing/${a.id}`}
                          data-testid={`pipeline-card-link-${a.id}`}
                          className="block"
                        >
                          <p className="font-serif-display font-bold text-[#f4efe6] text-base leading-tight">
                            {a.company_name}
                          </p>
                          <p className="mt-1 font-mono-eav text-[10px] text-[#5e5448] truncate">
                            {a.website || "—"}
                          </p>
                          <div className="mt-3 flex items-center justify-between">
                            <div className="flex items-center gap-1.5">
                              <span className="w-1.5 h-1.5 rounded-full" style={{ background: tcolor }} />
                              <span className="font-mono-eav text-[10px] tracking-wider uppercase" style={{ color: tcolor }}>
                                {a.tier_label || "—"}
                              </span>
                            </div>
                            {a.total_score != null && (
                              <span className="font-serif-display font-bold text-lg text-[#f4efe6]">
                                {a.total_score}<span className="text-[10px] text-[#5e5448] font-mono-eav">/100</span>
                              </span>
                            )}
                          </div>
                          <p className="mt-2 font-mono-eav text-[10px] text-[#5e5448]">
                            {new Date(a.created_at).toLocaleDateString()}
                          </p>
                        </Link>
                        {/* Move buttons */}
                        <div className="mt-3 pt-3 border-t border-[#1a1815] flex items-center justify-between">
                          <button
                            onClick={() => moveAudit(a.id, -1)}
                            disabled={colIdx === 0 || savingId === a.id}
                            data-testid={`pipeline-move-back-${a.id}`}
                            className="p-1 rounded-sm hover:bg-[#1a1815] text-[#8a7e6d] hover:text-[#b8963e] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                            title="Move back"
                          >
                            <ChevronLeft className="w-4 h-4" />
                          </button>
                          <span className="font-mono-eav text-[9px] tracking-wider text-[#5e5448] uppercase">
                            Move
                          </span>
                          <button
                            onClick={() => moveAudit(a.id, 1)}
                            disabled={colIdx === COLUMNS.length - 1 || savingId === a.id}
                            data-testid={`pipeline-move-forward-${a.id}`}
                            className="p-1 rounded-sm hover:bg-[#1a1815] text-[#8a7e6d] hover:text-[#b8963e] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                            title="Move forward"
                          >
                            <ChevronRight className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <p className="mt-10 font-mono-eav text-[11px] tracking-wider text-[#5e5448]">
        {total} TOTAL · {isAdmin && scope === "team" ? "ACROSS ALL USERS" : "YOUR AUDITS"} · Each snapshot is automatically added as "New" when generated.
      </p>
    </div>
  );
}
